import React, { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";

import { API, STRIPEPUBLICKEY } from "../../backend";
import authHeader from "../../services/auth-header";
import GetCompanyInfoService from "../../services/company/getCompanyInfoService";
import { C, S } from "./styles";

export { C, S };

// ─────────────────────────────────────────────────────────────────────────
// The two plans this product offers. Both this page (upgrade your own
// account) and AccountsPage.js's activation checkout read from here so the
// same two plans — nothing more — show up everywhere.
// ─────────────────────────────────────────────────────────────────────────
const PRO_PRICE = 100;

const FREE_FEATURES = [
  "Single user account",
  "Lifecycle Management",
  "ACH Payments",
  "Automated Accounting",
  "Dashboards and Reporting",
  "Customer Portal",
  "Priority online support",
];

const PRO_FEATURES = [
  "Unlimited user accounts",
  "Lifecycle Management",
  "ACH Payments",
  "Automated Accounting",
  "Dashboards and Reporting",
  "Customer Portal",
  "Priority online support",
];

// Loaded once and shared by every checkout step in this feature (Plans
// upgrade checkout, Accounts activation checkout) so Stripe.js itself is
// only fetched a single time.
const stripePromise = loadStripe(STRIPEPUBLICKEY);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REQUIRED_FIELD_LABELS = {
  firstName: "First name is required",
  lastName: "Last name is required",
  phone: "Contact number is required",
  companyName: "Company name is required",
};

// Shared Stripe card-entry + confirm step, used by both the Plans-tab
// upgrade checkout and the Accounts-tab client-activation checkout.
function PaymentStep({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try again.");
      setSubmitting(false);
      return;
    }
    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" ||
        paymentIntent.status === "requires_capture")
    ) {
      onSuccess(paymentIntent);
    } else {
      setError("Payment was not confirmed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <p style={{ color: "#dc2626", fontSize: 12.5, margin: "10px 0 0" }}>
          {error}
        </p>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting}
          style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
        >
          {submitting ? "Confirming Payment..." : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

// The one real "Subscriber's Details + Your Plan -> Payment -> Company
// Created" checkout flow. Used as-is from both PlansPage (Get Started,
// blank fields) and AccountsPage.js (Activate, fields pre-filled from the
// client that was already created) — nothing about this flow is
// duplicated or re-implemented elsewhere.
export function SubscriberCheckout({
  initialDetails,
  backLabel,
  onExit,
  onSuccess,
  createAccount,
  publicMode,
  skipEmailCheck,
  successNote,
  successTitle,
}) {
  // On the public sign-up page there's no logged-in user — and if the
  // visitor's browser happens to have a stale/invalid token left over in
  // localStorage from an earlier session, sending it as a bearer token
  // makes the backend reject even these otherwise-public endpoints
  // (401 "User not found") before it ever checks permissions. So in
  // publicMode we just never attach one.
  const requestHeaders = publicMode ? {} : authHeader();

  const [checkoutStep, setCheckoutStep] = useState("details"); // 'details' | 'payment' | 'success'
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [preparingPayment, setPreparingPayment] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [planPricing, setPlanPricing] = useState(null);
  const [clientSecret, setClientSecret] = useState("");

  const [subDetails, setSubDetails] = useState({
    firstName: (initialDetails && initialDetails.firstName) || "",
    lastName: (initialDetails && initialDetails.lastName) || "",
    email: (initialDetails && initialDetails.email) || "",
    phone: (initialDetails && initialDetails.phone) || "",
    companyName: (initialDetails && initialDetails.companyName) || "",
  });
  const [checkoutUsers, setCheckoutUsers] = useState(1);
  const [checkoutTerm, setCheckoutTerm] = useState(1);
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailChecking, setEmailChecking] = useState(false);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  useEffect(() => {
    let cancelled = false;
    setLoadingPlan(true);
    axios
      .get(API + "paymentsub/subplans/Annual/", { headers: requestHeaders })
      .then((res) => {
        if (!cancelled && res.data && res.data.success) {
          setPlanPricing(res.data.response);
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify(
            toast.error,
            "Couldn't load plan pricing for checkout. Please try again."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPlan(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToDetails = () => {
    setCheckoutStep("details");
    setClientSecret("");
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoChecking(true);
    setPromoError("");
    try {
      const listRes = await axios.get(
        API + "paymentsub/subscription/promo_code/",
        { headers: requestHeaders }
      );
      const codes = (listRes.data && listRes.data.data) || [];
      const match = codes.find((c) => c.promo_code === promoInput.trim());
      if (!match) {
        setPromoError("Invalid promotion code");
        setPromoApplied(null);
        return;
      }
      const detailRes = await axios.get(
        API + `paymentsub/subscription/promo_code/${match.id}`,
        { headers: requestHeaders }
      );
      if (detailRes.data && detailRes.data.success) {
        setPromoApplied(detailRes.data.data);
        notify(toast.success, "Promotion code applied");
      } else {
        setPromoError(
          (detailRes.data &&
            (detailRes.data.response || detailRes.data.error)) ||
            "Invalid promotion code"
        );
        setPromoApplied(null);
      }
    } catch (error) {
      setPromoError("Invalid promotion code");
      setPromoApplied(null);
    } finally {
      setPromoChecking(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
    setPromoError("");
  };

  const validateFields = () => {
    const errors = {};
    if (!subDetails.firstName.trim()) errors.firstName = REQUIRED_FIELD_LABELS.firstName;
    if (!subDetails.lastName.trim()) errors.lastName = REQUIRED_FIELD_LABELS.lastName;
    if (!subDetails.email.trim()) {
      errors.email = "Email is required";
    } else if (!EMAIL_RE.test(subDetails.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    if (!subDetails.phone.trim()) errors.phone = REQUIRED_FIELD_LABELS.phone;
    if (!subDetails.companyName.trim()) errors.companyName = REQUIRED_FIELD_LABELS.companyName;
    return errors;
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    setSubDetails((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleRequiredFieldBlur = (field) => () => {
    if (!subDetails[field].trim() && REQUIRED_FIELD_LABELS[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: REQUIRED_FIELD_LABELS[field] }));
    }
  };

  // Same endpoint (accounting/uservalidation/) the marketing site's
  // checkout uses, called the same way: live as the user types, and again
  // right before we let them proceed, so a copy/paste + immediate submit
  // can't skip the check.
  const checkEmailExists = async (email) => {
    // Upgrading an existing account's own plan pre-fills its own email —
    // of course that's already registered, so the "is this email taken"
    // check (meant for brand-new signups) doesn't apply here.
    if (skipEmailCheck) return true;
    try {
      const res = await axios.post(
        API + "accounting/uservalidation/",
        { email },
        { headers: requestHeaders }
      );
      if (res.data && res.data.success === 0) {
        const msg =
          res.data.response ||
          res.data.error ||
          res.data.message ||
          "This email is already registered";
        setFieldErrors((prev) => ({ ...prev, email: msg }));
        return false;
      }
      setFieldErrors((prev) => {
        if (!prev.email) return prev;
        const next = { ...prev };
        delete next.email;
        return next;
      });
      return true;
    } catch (error) {
      // Network hiccup on the live check — don't block typing; the
      // submit-time re-check below is the real gate.
      return true;
    }
  };

  useEffect(() => {
    const email = subDetails.email.trim();
    if (!email || !EMAIL_RE.test(email)) return undefined;
    setEmailChecking(true);
    const timer = setTimeout(() => {
      checkEmailExists(email).finally(() => setEmailChecking(false));
    }, 500);
    return () => {
      clearTimeout(timer);
      setEmailChecking(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subDetails.email]);

  const handleSubmitDetails = async (e) => {
    e.preventDefault();
    const errors = validateFields();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      notify(toast.error, "Please fix the highlighted fields");
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }
    setTermsError(false);
    if (totalPayable <= 0) {
      notify(toast.error, "Total payable must be greater than $0 to continue");
      return;
    }

    setPreparingPayment(true);
    try {
      const emailOk = await checkEmailExists(subDetails.email.trim());
      if (!emailOk) {
        notify(toast.error, "Please fix the highlighted fields");
        return;
      }
      const res = await axios.post(
        API + "paymentsub/subscription/create-payment-intent/",
        {
          amount: Math.round(totalPayable * 100),
          plan: (planPricing && planPricing.plan_name) || "Annual",
        },
        { headers: requestHeaders }
      );
      if (res.data && res.data.clientSecret) {
        setClientSecret(res.data.clientSecret);
        setCheckoutStep("payment");
      } else {
        notify(
          toast.error,
          (res.data && res.data.error) || "Couldn't start the payment. Please try again."
        );
      }
    } catch (error) {
      notify(toast.error, "Couldn't start the payment. Please try again.");
    } finally {
      setPreparingPayment(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setCreatingCompany(true);
    try {
      // `createAccount`, when passed in, lets a caller outside the
      // authenticated app (e.g. the public sign-up page) finalize the
      // account its own way instead of the normal authenticated
      // company/create-client/ call below — see PublicSignup.js.
      const resultData = createAccount
        ? await createAccount({
            subDetails,
            checkoutUsers,
            checkoutTerm,
            totalPayable,
            planPricing,
          })
        : (
            await axios.post(
              API + "company/create-client/",
              {
                company_name: subDetails.companyName.trim(),
                email: subDetails.email.trim(),
                contact_name: `${subDetails.firstName.trim()} ${subDetails.lastName.trim()}`.trim(),
                phone_number: subDetails.phone.trim(),
                plan: (planPricing && planPricing.plan_name) || "Annual",
                no_of_users: checkoutUsers,
                total_amount: totalPayable,
                trial_days: (planPricing && planPricing.trial_period) || 0,
                subscription_type: "STRIPE",
              },
              { headers: authHeader() }
            )
          ).data;

      if (resultData && resultData.success === 1) {
        setCreateResult(resultData);
        setCheckoutStep("success");
        if (onSuccess) onSuccess(resultData);
      } else {
        notify(
          toast.error,
          (resultData && resultData.error) ||
            "Payment succeeded, but the account couldn't be created. Please contact support."
        );
      }
    } catch (error) {
      const backendError =
        error.response && error.response.data && error.response.data.error;
      notify(
        toast.error,
        backendError ||
          "Payment succeeded, but the account couldn't be created. Please contact support."
      );
    } finally {
      setCreatingCompany(false);
    }
  };

  const perUserAmount = planPricing ? Number(planPricing.per_user_amount) : 100;
  const trialDays = planPricing ? Number(planPricing.trial_period) || 0 : 0;
  const pricePerYear = perUserAmount * 12;
  const subTotal = pricePerYear * checkoutUsers * checkoutTerm;
  const discountAmount = promoApplied
    ? promoApplied.discount_type === "amount_off"
      ? Number(promoApplied.discount_value)
      : (subTotal * Number(promoApplied.discount_value)) / 100
    : 0;
  const totalPayable = Math.max(subTotal - discountAmount, 0);
  const expiryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + trialDays);
    d.setFullYear(d.getFullYear() + checkoutTerm);
    return d;
  })();

  if (loadingPlan) {
    return <p style={S.subfootnote}>Loading...</p>;
  }

  return (
    <>
      {checkoutStep !== "success" && (
        <button
          type="button"
          style={S.backLink}
          onClick={checkoutStep === "payment" ? handleBackToDetails : onExit}
        >
          <i className="fa-solid fa-arrow-left" />
          {checkoutStep === "payment" ? "Back to Details" : backLabel}
        </button>
      )}

      {checkoutStep === "details" ? (
        <form onSubmit={handleSubmitDetails}>
          <div style={S.checkoutGrid}>
            <div style={S.panel}>
              <h3 style={S.planTitle}>
                <span style={{ ...S.dot, background: C.success }} />
                Subscriber's Details
              </h3>
              <p style={S.planDesc}>
                We make payments easy. No need to negotiate with a sales team.
              </p>
              <div style={S.formGroup}>
                <label style={S.formLabel}>First Name *</label>
                <input
                  style={{
                    ...S.formInput,
                    ...(fieldErrors.firstName ? S.formInputError : {}),
                  }}
                  value={subDetails.firstName}
                  onChange={handleFieldChange("firstName")}
                  onBlur={handleRequiredFieldBlur("firstName")}
                />
                {fieldErrors.firstName && (
                  <p style={S.fieldErrorText}>{fieldErrors.firstName}</p>
                )}
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>Last Name *</label>
                <input
                  style={{
                    ...S.formInput,
                    ...(fieldErrors.lastName ? S.formInputError : {}),
                  }}
                  value={subDetails.lastName}
                  onChange={handleFieldChange("lastName")}
                  onBlur={handleRequiredFieldBlur("lastName")}
                />
                {fieldErrors.lastName && (
                  <p style={S.fieldErrorText}>{fieldErrors.lastName}</p>
                )}
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>Email ID *</label>
                <input
                  type="email"
                  style={{
                    ...S.formInput,
                    ...(fieldErrors.email ? S.formInputError : {}),
                  }}
                  value={subDetails.email}
                  onChange={handleFieldChange("email")}
                />
                {emailChecking && <p style={S.subfootnote}>Checking email...</p>}
                {fieldErrors.email && (
                  <p style={S.fieldErrorText}>{fieldErrors.email}</p>
                )}
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>Contact Number *</label>
                <input
                  style={{
                    ...S.formInput,
                    ...(fieldErrors.phone ? S.formInputError : {}),
                  }}
                  value={subDetails.phone}
                  onChange={handleFieldChange("phone")}
                  onBlur={handleRequiredFieldBlur("phone")}
                />
                {fieldErrors.phone && (
                  <p style={S.fieldErrorText}>{fieldErrors.phone}</p>
                )}
              </div>
              <div style={{ ...S.formGroup, marginBottom: 0 }}>
                <label style={S.formLabel}>Company Name *</label>
                <input
                  style={{
                    ...S.formInput,
                    ...(fieldErrors.companyName ? S.formInputError : {}),
                  }}
                  value={subDetails.companyName}
                  onChange={handleFieldChange("companyName")}
                  onBlur={handleRequiredFieldBlur("companyName")}
                />
                {fieldErrors.companyName && (
                  <p style={S.fieldErrorText}>{fieldErrors.companyName}</p>
                )}
              </div>
            </div>

            <div style={S.panel}>
              <h3 style={S.planTitle}>
                <span style={{ ...S.dot, background: "#ef4444" }} />
                Your Plan
              </h3>
              <p style={S.planDesc}>Please check your subscription cost carefully.</p>

              <div style={S.planRow}>
                <span>Subscription Cost per User</span>
                <strong>${perUserAmount}/Month</strong>
              </div>
              <div style={S.planRow}>
                <span>Number of Users</span>
                <select
                  style={{ ...S.formInput, width: "auto" }}
                  value={checkoutUsers}
                  onChange={(e) => setCheckoutUsers(Number(e.target.value))}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n} User{n > 1 ? "s" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div style={S.planRow}>
                <div>
                  <div>Term</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    Expires on {expiryDate.toLocaleDateString()}
                  </div>
                </div>
                <select
                  style={{ ...S.formInput, width: "auto" }}
                  value={checkoutTerm}
                  onChange={(e) => setCheckoutTerm(Number(e.target.value))}
                >
                  <option value={1}>1 Year (12 Months)</option>
                  <option value={2}>2 Years (24 Months)</option>
                  <option value={3}>3 Years (36 Months)</option>
                </select>
              </div>
              <div style={S.planRow}>
                <span>Add-ons</span>
                <span>$0.00</span>
              </div>
              <div style={S.planRow}>
                <span>Promotion Code</span>
                <div style={{ display: "flex", gap: 8 }}>
                  {promoApplied ? (
                    <span style={S.tagPromo}>
                      {promoApplied.promo_code}
                      <button
                        type="button"
                        style={S.tagRemove}
                        onClick={handleRemovePromo}
                      >
                        &times;
                      </button>
                    </span>
                  ) : (
                    <>
                      <input
                        style={{ ...S.formInput, width: 140 }}
                        placeholder="Enter code"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value)}
                      />
                      <button
                        type="button"
                        style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
                        disabled={!promoInput.trim() || promoChecking}
                        onClick={handleApplyPromo}
                      >
                        {promoChecking ? "..." : "Apply"}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {promoError && (
                <p style={{ color: "#dc2626", fontSize: 12, margin: "4px 0 0" }}>
                  {promoError}
                </p>
              )}
              <div style={S.planRow}>
                <span>Discount</span>
                <span>${discountAmount.toFixed(2)}</span>
              </div>
              <div style={{ ...S.planRow, borderBottom: "none" }}>
                <strong>Total Payable</strong>
                <strong style={{ fontSize: 18 }}>${totalPayable.toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <div style={S.termsBox}>
            <label
              style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked) setTermsError(false);
                }}
                style={{ marginTop: 3 }}
              />
              <span>
                I agree to all the Terms and Conditions defined here.
                {planPricing && (
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                    Total payable amount will be charged after {trialDays} days of
                    free trial. You can cancel anytime before the trial ends.
                  </div>
                )}
              </span>
            </label>
            {termsError && (
              <p style={S.fieldErrorText}>Please accept the terms and conditions</p>
            )}
            <button
              type="submit"
              disabled={preparingPayment}
              style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary, marginTop: 14 }}
            >
              {preparingPayment ? "Preparing Payment..." : "Continue to Payment"}
            </button>
          </div>
        </form>
      ) : checkoutStep === "payment" ? (
        <div style={S.panel}>
          <h3 style={S.planTitle}>
            <span style={{ ...S.dot, background: C.success }} />
            Payment
          </h3>
          <p style={S.planDesc}>
            Your company will only be created once this payment is confirmed.
          </p>
          <div style={S.planRow}>
            <span>Company</span>
            <strong>{subDetails.companyName}</strong>
          </div>
          <div style={{ ...S.planRow, borderBottom: "none" }}>
            <span>Amount due today</span>
            <strong>${totalPayable.toFixed(2)}</strong>
          </div>
          <div style={{ marginTop: 18 }}>
            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentStep
                  amount={totalPayable}
                  onCancel={handleBackToDetails}
                  onSuccess={handlePaymentSuccess}
                />
              </Elements>
            )}
          </div>
          {creatingCompany && (
            <p style={{ ...S.subfootnote, textAlign: "left", marginTop: 14 }}>
              Payment confirmed — creating your company...
            </p>
          )}
        </div>
      ) : (
        <div style={S.panel}>
          <h3 style={S.planTitle}>
            <span style={{ ...S.dot, background: C.success }} />
            {successTitle || "Company Created"}
          </h3>
          <p style={S.planDesc}>{createResult && createResult.message}</p>
          <div style={S.planRow}>
            <span>Company</span>
            <strong>{createResult && createResult.client_company_name}</strong>
          </div>
          <div style={S.planRow}>
            <span>Login email</span>
            <strong>{createResult && createResult.client_email}</strong>
          </div>
          {createResult && createResult.subscription_num && (
            <div style={{ ...S.planRow, borderBottom: "none" }}>
              <span>Subscription #</span>
              <strong>{createResult.subscription_num}</strong>
            </div>
          )}
          <p style={{ ...S.subfootnote, textAlign: "left", marginTop: 14 }}>
            {successNote || "Login credentials have been emailed to the address above."}
          </p>
          <button
            type="button"
            style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary, marginTop: 10 }}
            onClick={onExit}
          >
            Done
          </button>
        </div>
      )}
    </>
  );
}

// The Free / Professional plan card grid — the single source of truth for
// this UI. Used as-is both by PlansPage below (the in-app, authenticated
// Plans tab) and by PublicSignup.js (the public, no-login sign-up page),
// so the two never drift apart. Button labels/behavior are fully
// prop-driven since the two callers need different actions on click.
export function PlanCards({
  freeButtonLabel = "Get Started Free",
  freeButtonDisabled = false,
  freeButtonClassName,
  onFreeClick,
  proButtonLabel = "Get Started",
  proButtonDisabled = false,
  proButtonClassName,
  onProClick,
}) {
  return (
    <>
      <div style={S.plansGrid}>
        <div className="card h-100 border-0 shadow-sm">
          <div className="card-body d-flex flex-column">
            <h5 className="fw-bold mb-2">Free</h5>
            <p className="text-muted small mb-3">
              Discover the power of Athena's Lease, Loan and Rental Management platform for
              Lessors for free.
            </p>
            <hr className="mt-0" />
            <div className="d-flex align-items-baseline gap-2">
              <span className="small fw-semibold text-muted">USD</span>
              <span className="fs-2 fw-bold">0</span>
              <span className="small text-muted">/ user / month</span>
            </div>
            <div className="small mb-3" style={{ visibility: "hidden" }}>
              &nbsp;
            </div>
            <ul className="list-unstyled flex-grow-1 mb-4">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="d-flex align-items-center gap-2 mb-2 small">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 16, height: 16, fontSize: 9, background: "#d1fae5", color: "#10b981" }}
                  >
                    <i className="fa-solid fa-check" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className={freeButtonClassName || "btn btn-outline-secondary"}
              disabled={freeButtonDisabled}
              onClick={onFreeClick}
            >
              {freeButtonLabel}
            </button>
          </div>
        </div>

        <div className="card h-100 shadow position-relative" style={{ borderColor: "#245C7F", borderWidth: 2 }}>
          <span
            className="badge position-absolute top-0 start-50 translate-middle text-uppercase"
            style={{ background: "#245C7F", color: "#fff" }}
          >
            Most Popular
          </span>
          <div className="card-body d-flex flex-column">
            <h5 className="fw-bold mb-2">Professional</h5>
            <p className="text-muted small mb-3">
              Discover the power of Athena's Lease, Loan and Rental Management platform for
              Lessors at an unbeatable price.
            </p>
            <hr className="mt-0" />
            <div className="d-flex align-items-baseline gap-2">
              <span className="small fw-semibold text-muted">USD</span>
              <span className="fs-2 fw-bold">{PRO_PRICE}</span>
              <span className="small text-muted">/ user / month</span>
            </div>
            <div className="small text-muted mb-3">(payable annual in advance)</div>
            <ul className="list-unstyled flex-grow-1 mb-4">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="d-flex align-items-center gap-2 mb-2 small">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 16, height: 16, fontSize: 9, background: "#eff6ff", color: "#245C7F" }}
                  >
                    <i className="fa-solid fa-check" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className={proButtonClassName || "border-0 btn btn-primary btn-gradient-primary"}
              disabled={proButtonDisabled}
              onClick={onProClick}
            >
              {proButtonLabel}
            </button>
          </div>
        </div>
      </div>

      <div className="text-center text-muted small mt-3">
        <i className="fa-solid fa-shield-halved me-2" style={{ color: "#245C7F" }} />
        All plans include secure hosting, automatic backups, and 99.9% uptime.
      </div>
    </>
  );
}

function PlansPage() {
  const [showCheckout, setShowCheckout] = useState(false);
  const [existingSubscription, setExistingSubscription] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  const fetchOwnAccount = () => {
    axios
      .get(API + "paymentsub/subscription/", { headers: authHeader() })
      .then((res) => {
        if (res.data && res.data.success === 1 && res.data.data) {
          setExistingSubscription(res.data.data);
        }
      })
      .catch(() => {});
    GetCompanyInfoService.getCompanyInfo()
      .then((res) => setCompanyProfile(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchOwnAccount();
  }, []);

  const perUserAmount = existingSubscription && existingSubscription.plan
    ? Number(existingSubscription.plan.per_user_amount) || 0
    : 0;
  const currentPlan = perUserAmount > 0 ? "pro" : "free";

  const handleDowngrade = () => {
    notify(toast.info, "To downgrade to the Free plan, please contact support.");
  };

  // Pre-fill the checkout with the details of the account that's already
  // there — this is an existing company upgrading its own plan, not a
  // brand new signup, so the visitor shouldn't have to retype anything
  // just to get to the payment step.
  const initialDetails = companyProfile
    ? {
        firstName: (companyProfile.name || "").trim().split(/\s+/)[0] || "",
        lastName: (companyProfile.name || "").trim().split(/\s+/).slice(1).join(" "),
        email: companyProfile.user_email || "",
        phone: companyProfile.phone_number || "",
        companyName: companyProfile.company_name || "",
      }
    : {};

  // Upgrading updates the EXISTING subscription row in place (plan,
  // no_of_users, total_amount, a fresh start_date) rather than going
  // through the default company/create-client/ path, which creates a
  // brand new company — wrong here, since this account already exists.
  const upgradeExistingSubscription = async ({ subDetails, checkoutUsers, totalPayable, planPricing }) => {
    if (!existingSubscription || !planPricing) {
      return {
        success: 0,
        error: "Couldn't find your subscription to upgrade. Please contact support.",
      };
    }
    try {
      const res = await axios.put(
        API + "paymentsub/subscription/",
        {
          id: existingSubscription.id,
          plan_id: planPricing.id,
          no_of_users: checkoutUsers,
          total_amount: totalPayable,
          subscription_type: "STRIPE",
          start_date: new Date().toISOString().slice(0, 10),
        },
        { headers: authHeader() }
      );
      if (!res.data || res.data.success !== 1) {
        return {
          success: 0,
          error: (res.data && res.data.error) || "Couldn't upgrade your subscription.",
        };
      }

      // Log it in Payment History too — the PUT above only updates the
      // subscription itself, it doesn't create a SubPayments row on its
      // own (same pattern the Manage License add/reduce-licenses flow
      // uses: update the subscription, then separately record the charge).
      try {
        await axios.post(
          API + "paymentsub/subscription/payment/",
          {
            sub_id: existingSubscription.id,
            payment_date: new Date().toISOString().slice(0, 10),
            payment_method: "STRIPE",
            payment_gateway: "STRIPE",
            payment_status: "success",
            no_of_user: checkoutUsers,
            total_amount: totalPayable,
          },
          { headers: authHeader() }
        );
      } catch (logError) {
        // The upgrade itself already succeeded — don't fail the whole
        // flow just because the history entry couldn't be written.
      }

      return {
        success: 1,
        message: "Your subscription has been upgraded.",
        client_company_name: subDetails.companyName,
        client_email: subDetails.email,
        subscription_num: res.data.data && res.data.data.sub_num,
      };
    } catch (error) {
      return { success: 0, error: "Couldn't upgrade your subscription. Please contact support." };
    }
  };

  if (showCheckout) {
    return (
      <SubscriberCheckout
        initialDetails={initialDetails}
        backLabel="Back to Plans"
        onExit={() => setShowCheckout(false)}
        onSuccess={() => fetchOwnAccount()}
        createAccount={upgradeExistingSubscription}
        skipEmailCheck
        successTitle="Subscription Upgraded"
        successNote="Your existing account has been upgraded — no new login is needed."
      />
    );
  }

  return (
    <>
      <PlanCards
        freeButtonLabel={currentPlan === "free" ? "Current" : "Downgrade"}
        freeButtonDisabled={currentPlan === "free"}
        freeButtonClassName={currentPlan === "free" ? "btn btn-secondary" : "btn btn-outline-secondary"}
        onFreeClick={handleDowngrade}
        proButtonLabel={currentPlan === "pro" ? "Current" : "+ Upgrade"}
        proButtonDisabled={currentPlan === "pro"}
        proButtonClassName={
          currentPlan === "pro" ? "btn btn-secondary" : "border-0 btn btn-primary btn-gradient-primary"
        }
        onProClick={() => setShowCheckout(true)}
      />
      <div className="text-center text-muted small mt-3">Prices are in USD and billed monthly.</div>
    </>
  );
}

export default PlansPage;
